// Columna central de Tiendas: píldoras, conteo y la tabla de 11 columnas del mock.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UserCheck, UserX } from 'lucide-react';
import { Button, Tabs } from '@/kit';
import { StatusPill } from '@/shared/patterns/Pills';
import { DataTable, UnavailableState, type DataTableColumn } from '@/shared/patterns/DataTable';
import { StatsBar, type StatsBarTone } from '@/shared/patterns/StatsBar';
import { reportPeriodLabel, type ReportPeriod } from '@/shared/patterns/PeriodBand';
import {
    isInactiveStore,
    storeAlerts,
    storeGoalProgress,
    storePillCounts,
    storeKindLabel,
    tenantShare,
    type StoreContext,
    type StorePill,
    type StoreRow,
    type StoreSort,
    type StoreSortKey,
    type StoreStatusTab,
} from '../lib/storesTable';
import { money } from '../lib/tiendasFormat';
import { AlertBadges, GoalBar, RankMedal, WeekDelta } from './StoreBadges';

const PILLS: { key: StorePill; label: string; tone: StatsBarTone; title: string }[] = [
    { key: 'all', label: 'Todas', tone: 'neutral', title: 'Todas las tiendas del padrón' },
    {
        key: 'withSales',
        label: 'Con ventas',
        tone: 'success',
        title: 'Tiendas con al menos una venta en el periodo',
    },
    {
        key: 'attention',
        label: 'Necesitan atención',
        tone: 'danger',
        title: 'Más de 3 días sin venta o caída mayor al 20% vs la semana anterior',
    },
    {
        key: 'top',
        label: 'Top 25%',
        tone: 'success',
        title: 'El cuartil superior por colocación entre las tiendas que vendieron',
    },
    {
        key: 'noTeam',
        label: 'Sin equipo activo',
        tone: 'warning',
        title: 'Tiendas sin vendedores activos en el padrón',
    },
];

const STATUS_TABS: readonly StoreStatusTab[] = ['ACTIVE', 'SUSPENDED', 'INACTIVE', 'ALL'];

function isStatusTab(key: string): key is StoreStatusTab {
    return STATUS_TABS.some((tab) => tab === key);
}

/** Pestañas por estatus del padrón con su conteo. */
export function StoreStatusTabs({
    active,
    counts,
    onChange,
}: {
    active: StoreStatusTab;
    counts: Record<StoreStatusTab, number> | null;
    onChange: (tab: StoreStatusTab) => void;
}) {
    const { t } = useTranslation('stores');
    return (
        <Tabs
            wrap
            className="flex-none px-2"
            tabs={STATUS_TABS.map((tab) => {
                const label = t(`statusTabs.${tab}`);
                return {
                    key: tab,
                    label: counts
                        ? t('statusTabs.withCount', { label, count: counts[tab] })
                        : label,
                };
            })}
            activeTab={active}
            onChange={(key) => {
                if (isStatusTab(key)) onChange(key);
            }}
        />
    );
}

function ShareCell({ share }: { share: number | null }) {
    if (share === null) return <span className="text-muted-foreground">—</span>;
    const pct = Math.round(share * 100);
    return (
        <div className="flex items-center gap-1">
            <div className="h-[5px] flex-1 overflow-hidden rounded-full bg-muted">
                <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${String(Math.min(pct, 100))}%` }}
                />
            </div>
            <span className="w-7 text-right text-[10px] font-semibold tabular-nums">{pct}%</span>
        </div>
    );
}

/** `activos/total` y un icono de admin, en una línea. */
function TeamCell({ row }: { row: StoreRow }) {
    const Icon = row.managerId === null ? UserX : UserCheck;
    return (
        <span className="inline-flex items-center gap-1">
            <span className="font-semibold tabular-nums">
                {row.activeSellers}
                {row.teamTotalCount !== null ? (
                    <span className="font-normal text-muted-foreground">/{row.teamTotalCount}</span>
                ) : null}
            </span>
            <Icon
                className={row.managerId === null ? 'size-3 text-warning' : 'size-3 text-info'}
                aria-label={row.managerId === null ? 'Sin admin' : 'Con admin'}
            />
        </span>
    );
}

export function StoresTable({
    rows,
    allRows,
    context,
    sort,
    onSortToggle,
    pill,
    onPillChange,
    selectedId,
    onSelect,
    isLoading,
    error,
    onRetry,
    period,
    teamAvailable,
    actions,
    statusTabs,
    hasAnyStore,
}: {
    rows: StoreRow[];
    allRows: StoreRow[];
    context: StoreContext;
    sort: StoreSort;
    onSortToggle: (key: StoreSortKey) => void;
    pill: StorePill;
    onPillChange: (pill: StorePill) => void;
    selectedId: string | null;
    onSelect: (storeId: string) => void;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    period: ReportPeriod;
    teamAvailable: boolean;
    /** Botones de la barra de la tabla (p. ej. «+ Nueva tienda»). */
    actions?: ReactNode;
    /** Pestañas por estatus, arriba de las píldoras. */
    statusTabs?: ReactNode;
    /** La cadena tiene tiendas aunque el estatus elegido no tenga ninguna. */
    hasAnyStore?: boolean;
}) {
    const { t } = useTranslation('stores');
    const counts = storePillCounts(allRows, context);
    const periodSub = reportPeriodLabel(period).toLowerCase();
    const allPeriod = period === 'all';

    const columns: DataTableColumn<StoreRow, StoreSortKey>[] = [
        {
            key: 'code',
            label: 'Clave',
            width: '54px',
            sortKey: 'code',
            cellClassName: () => 'font-mono text-[10.5px] font-semibold',
            cellTitle: (row) => row.code ?? 'Sin clave en el padrón',
            cell: (row) => row.code ?? '—',
        },
        {
            key: 'name',
            label: 'Tienda',
            sortKey: 'name',
            cellTitle: (row) =>
                [row.name, row.zoneName, row.kind ? storeKindLabel(row.kind) : null]
                    .filter(Boolean)
                    .join(' · '),
            cell: (row) => (
                <span className="flex min-w-0 items-center gap-1">
                    <RankMedal rank={context.ranks.get(row.storeId)} />
                    <span className="truncate font-semibold">{row.name}</span>
                    {isInactiveStore(row) && row.status ? (
                        <StatusPill tone="neutral" className="flex-none">
                            {t(`status.${row.status}`)}
                        </StatusPill>
                    ) : null}
                </span>
            ),
        },
        {
            key: 'zone',
            label: t('zone.column'),
            width: '84px',
            sortKey: 'zone',
            cellTitle: (row) => row.zoneName ?? t('zone.none'),
            cell: (row) =>
                row.zoneName ? (
                    row.zoneName
                ) : (
                    <span className="text-muted-foreground">{t('zone.none')}</span>
                ),
        },
        {
            key: 'alerts',
            label: 'Alertas',
            width: '58px',
            sortKey: 'alerts',
            cellOverflow: 'nowrap',
            cell: (row) => <AlertBadges alerts={storeAlerts(row, context)} />,
        },
        {
            key: 'team',
            label: 'Equipo',
            width: '56px',
            numeric: true,
            sortKey: 'team',
            note: 'Vendedores activos de la tienda sobre el total de su equipo, y si tiene admin',
            cellOverflow: 'nowrap',
            cellTitle: (row) =>
                row.activeSellers === null
                    ? undefined
                    : row.managerId === null
                      ? 'Sin admin asignado'
                      : `Admin: ${row.managerName ?? 'sin nombre en el padrón'}`,
            cell: (row) =>
                !teamAvailable || row.activeSellers === null ? (
                    <span className="text-muted-foreground">—</span>
                ) : (
                    <TeamCell row={row} />
                ),
        },
        {
            key: 'placed',
            label: 'Colocación',
            width: '88px',
            numeric: true,
            sortKey: 'placed',
            note: `Colocación del periodo (${periodSub})`,
            cellClassName: () => 'font-bold',
            cellTitle: (row) => money(row.placedCents),
            cell: (row) => money(row.placedCents),
        },
        {
            key: 'weekOverWeek',
            label: 'Δ sem',
            width: '54px',
            numeric: true,
            sortKey: 'weekOverWeek',
            note: 'Colocación de los últimos 7 días contra los 7 anteriores',
            cellOverflow: 'nowrap',
            cell: (row) => <WeekDelta pct={row.weekOverWeekPct} isNew={row.weekIsNew} />,
        },
        {
            key: 'sales',
            label: 'Ventas',
            width: '46px',
            numeric: true,
            sortKey: 'sales',
            note: `Ventas del periodo (${periodSub})`,
            cell: (row) => row.salesCount ?? '—',
        },
        {
            key: 'goal',
            label: '% Meta',
            width: '66px',
            sortKey: 'goal',
            note: 'Cumplimiento de la meta en pesos del periodo',
            cellOverflow: 'nowrap',
            cell: (row) =>
                allPeriod ? (
                    <span
                        className="text-[10px] italic text-muted-foreground"
                        title="Sin meta en modo Todo"
                    >
                        n/a
                    </span>
                ) : (
                    <GoalBar
                        inline
                        progress={storeGoalProgress(row)}
                        title={`${money(row.placedCents)} de ${money(row.goalCents)}`}
                    />
                ),
        },
        {
            key: 'share',
            label: '% cadena',
            width: '64px',
            sortKey: 'share',
            note: 'Colocación de la tienda sobre la colocación total de la cadena',
            cellOverflow: 'nowrap',
            cell: (row) => <ShareCell share={tenantShare(row, context)} />,
        },
    ];

    return (
        <>
            {statusTabs}
            <StatsBar
                items={PILLS.map((p) => {
                    const off = p.key === 'noTeam' && !teamAvailable;
                    return {
                        key: p.key,
                        label: p.label,
                        tone: p.tone,
                        title: off ? 'El padrón de vendedores no respondió' : p.title,
                        disabled: off || isLoading || error !== null,
                        value: off || isLoading || error ? '—' : counts[p.key],
                    };
                })}
                active={pill}
                onChange={onPillChange}
                ariaLabel="Segmentos de tiendas"
            />
            <div className="flex flex-none flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/50 px-3 py-1">
                <span className="text-[10.5px] font-semibold text-muted-foreground">
                    {isLoading || error
                        ? '— tiendas'
                        : `${String(rows.length)} ${rows.length === 1 ? 'tienda' : 'tiendas'}`}
                </span>
                {actions ? (
                    <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                        {actions}
                    </span>
                ) : null}
            </div>

            {error ? (
                <UnavailableState
                    title="No se pudieron cargar las tiendas"
                    body={error.message}
                    action={
                        <Button variant="outline" size="sm" onClick={onRetry}>
                            Reintentar
                        </Button>
                    }
                />
            ) : (
                <DataTable
                    columns={columns}
                    rows={rows}
                    rowKey={(row) => row.storeId}
                    sort={sort}
                    onSortToggle={onSortToggle}
                    density="compact"
                    caption="Tiendas del periodo con clave, zona, alertas, equipo, colocación, cambio semanal, ventas, cumplimiento de meta y participación en la cadena."
                    isRowSelected={(row) => row.storeId === selectedId}
                    onRowClick={(row) => {
                        onSelect(row.storeId);
                    }}
                    isLoading={isLoading}
                    empty={
                        allRows.length === 0 && !hasAnyStore ? (
                            'Tu cadena todavía no tiene tiendas dadas de alta.'
                        ) : (
                            <span className="italic">
                                Sin tiendas con los filtros seleccionados.
                            </span>
                        )
                    }
                />
            )}
        </>
    );
}
